import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import {
  IconButton,
  Typography,
  Box
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Toast from '../../components/Toast';

// Iconos SVG personalizados
const EmailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.8809 6.77222L12.0137 11.7833C11.7468 11.9384 11.4435 12.0201 11.1348 12.0201C10.8261 12.0201 10.5228 11.9384 10.2559 11.7833L2.38086 6.77222M4.13086 4.14722H18.1309C19.0974 4.14722 19.8809 4.93072 19.8809 5.89722V16.3972C19.8809 17.3637 19.0974 18.1472 18.1309 18.1472H4.13086C3.16436 18.1472 2.38086 17.3637 2.38086 16.3972V5.89722C2.38086 4.93072 3.16436 4.14722 4.13086 4.14722Z" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.1309 15.9863H11.1396M6.75586 1.98633H15.5059C16.4724 1.98633 17.2559 2.76983 17.2559 3.73633V17.7363C17.2559 18.7028 16.4724 19.4863 15.5059 19.4863H6.75586C5.78936 19.4863 5.00586 18.7028 5.00586 17.7363V3.73633C5.00586 2.76983 5.78936 1.98633 6.75586 1.98633Z" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="25" height="28" viewBox="0 0 25 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5584 9.60339H6.82297C5.89707 9.60339 5.14648 10.354 5.14648 11.2799V17.1476C5.14648 18.0735 5.89707 18.8241 6.82297 18.8241H18.5584C19.4843 18.8241 20.2348 18.0735 20.2348 17.1476V11.2799C20.2348 10.354 19.4843 9.60339 18.5584 9.60339Z" stroke="black" strokeWidth="0.914446" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.49902 9.60351V6.25054C8.49902 5.13896 8.9406 4.07291 9.7266 3.2869C10.5126 2.5009 11.5787 2.05933 12.6902 2.05933C13.8018 2.05933 14.8679 2.5009 15.6539 3.2869C16.4399 4.07291 16.8814 5.13896 16.8814 6.25054V9.60351" stroke="black" strokeWidth="0.914446" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Input controlado, memoizado
const CustomInput = memo(({ label, value, onChange, type = "text", placeholder = "", showPassword = false, onTogglePassword }) => {
  const resolvedType = type === "password" ? (showPassword ? "text" : "password") : type;

  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '8px'
      }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={resolvedType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '12px 16px',
            paddingRight: type === 'password' ? '50px' : '16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '16px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: 'white',
            transition: 'border-color 0.2s',
            outline: 'none'
          }}
          onFocus={(e) => { e.target.style.borderColor = '#007bff'; }}
          onBlur={(e) => { e.target.style.borderColor = '#ddd'; }}
        />
        {type === 'password' && (
          <button
            type="button"
            onClick={onTogglePassword}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '24px',
              width: '24px'
            }}
          >
            {showPassword ? <VisibilityOffIcon sx={{ fontSize: '20px' }} /> : <VisibilityIcon sx={{ fontSize: '20px' }} />}
          </button>
        )}
      </div>
    </div>
  );
});

// Helper para controlar el padding vertical de los modales (altura desde arriba)
const getTopModalPadding = (topPosition) => {
  return `${topPosition}px 20px 20px`;
};
// Modal memoizado
const CustomModal = memo(({
  isOpen,
  onClose,
  title,
  children,
  onSave,
  saveText = "Guardar",
  isLoading = false,
  isFormValid = () => true,
  alignTop = false,
  topPosition = 120
}) => {
  return (
    <div
      style={{
        display: isOpen ? "flex" : "none",
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 9999,
        padding: getTopModalPadding(topPosition)
      }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '400px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 20px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#000' }}>
            {title}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#666', padding: '4px' }}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {children}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0 20px 20px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onSave}
            disabled={isLoading || !isFormValid()}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: isLoading || !isFormValid() ? '#ccc' : '#007bff',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isLoading || !isFormValid() ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Guardando...' : saveText}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#666',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
});

const Ajustes = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  const {
    email: emailContext,
    celular: celularContext,
    nombre: nombreContext,
    apellido: apellidoContext,
    isDataLoaded,
    updateLocalData
  } = useUser();
  const [userData, setUserData] = useState({ email: '', celular: '', nombre: '', apellido: '' });
  const [, setLoading] = useState(true);

  const emailButtonRef = useRef(null);
  const [modalTopPosition, setModalTopPosition] = useState(120);

  // Modales
  const [emailModal, setEmailModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [phoneModal, setPhoneModal] = useState(false);

  // Formularios
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [confirmPhone, setConfirmPhone] = useState('');
  const [showPasswords, setShowPasswords] = useState({ new: false, confirm: false });
  const [phoneError, setPhoneError] = useState('');
  const [confirmPhoneError, setConfirmPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [confirmEmailError, setConfirmEmailError] = useState('');

  // Mensajes
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  // Modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState({});

  const baseURL = process.env.REACT_APP_BASE_URL;

  // Cargar datos desde el contexto cuando estén disponibles
  useEffect(() => {
    if (isDataLoaded) {
      setUserData({
        email: emailContext || '',
        celular: celularContext || '',
        nombre: nombreContext || '',
        apellido: apellidoContext || ''
      });
      setNewEmail('');
      setConfirmEmail('');
      setNewPhone('');
      setConfirmPhone('');
      setLoading(false);
    }
  }, [isDataLoaded, emailContext, celularContext, nombreContext, apellidoContext]);

  useEffect(() => {
    // Kickout solo si Firebase confirma que no hay usuario autenticado
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
      return;
    }
  }, [navigate, user, authLoading]);

  const handleLogout = () => {
    document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    localStorage.removeItem('jwt');
    navigate("/authentication/sign-in");
  };

  const togglePasswordVisibility = useCallback((field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  // Función para validar teléfono en tiempo real
  const validatePhone = useCallback((phone, setErrorFunction) => {
    // Solo permitir números
    const numericPhone = phone.replace(/\D/g, '');

    // Limitar a 9 dígitos máximo
    const limitedPhone = numericPhone.slice(0, 9);

    // Validar formato
    const phoneRegex = /^09\d{7}$/;
    let error = '';

    if (limitedPhone.length > 0) {
      if (!limitedPhone.startsWith('09')) {
        error = 'El número debe empezar con 09';
      } else if (limitedPhone.length < 9) {
        error = `Faltan ${9 - limitedPhone.length} dígitos`;
      } else if (!phoneRegex.test(limitedPhone)) {
        error = 'Formato inválido';
      }
    }

    setErrorFunction(error);
    return limitedPhone;
  }, []);

  // Función para mostrar mensajes
  const showMessage = useCallback((text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  }, []);

  // Función para validar formato de email estricto
  const validateEmailFormat = useCallback((email) => {
    // Debe tener exactamente: texto + "@" + texto + "." + texto
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }, []);

  // Función para validar email en tiempo real
  const validateEmailRealTime = useCallback((email, setErrorFunction) => {
    if (!email.trim()) {
      setErrorFunction('');
      return true; // Campo vacío es válido (no requerido hasta guardar)
    }

    const isValid = validateEmailFormat(email);
    if (!isValid) {
      setErrorFunction('Formato requerido: correo@mail.com');
    } else {
      setErrorFunction('');
    }
    return isValid;
  }, [validateEmailFormat]);

  // Función para verificar si el formulario de email es válido
  const isEmailFormValid = useCallback(() => {
    const newEmailValid = !newEmail.trim() || validateEmailFormat(newEmail);
    const confirmEmailValid = !confirmEmail.trim() || validateEmailFormat(confirmEmail);
    const emailsMatch = !newEmail.trim() || !confirmEmail.trim() || newEmail === confirmEmail;
    const differentFromCurrent = !newEmail.trim() || newEmail !== userData.email;

    return newEmailValid && confirmEmailValid && emailsMatch && differentFromCurrent && newEmail.trim() && confirmEmail.trim();
  }, [newEmail, confirmEmail, userData.email, validateEmailFormat]);

  // Función para verificar si el formulario de contraseña es válido
  const isPasswordFormValid = useCallback(() => {
    const passwordValid = newPassword.trim().length >= 6;
    const passwordsMatch = newPassword === confirmPassword;

    return passwordValid && passwordsMatch && newPassword.trim();
  }, [newPassword, confirmPassword]);

  // Función para verificar si el formulario de teléfono es válido
  const isPhoneFormValid = useCallback(() => {
    const phoneRegex = /^09\d{7}$/;

    const newPhoneValid = phoneRegex.test(newPhone);
    const confirmPhoneValid = phoneRegex.test(confirmPhone);
    const phonesMatch = newPhone === confirmPhone;
    const differentFromCurrent = newPhone !== userData.celular;
    const noErrors = !phoneError && !confirmPhoneError;

    return newPhoneValid && confirmPhoneValid && phonesMatch && differentFromCurrent && noErrors && newPhone.trim() && confirmPhone.trim();
  }, [newPhone, confirmPhone, userData.celular, phoneError, confirmPhoneError]);

  // Función para confirmar guardar email
  const handleConfirmSaveEmail = useCallback(() => {
    if (!isEmailFormValid()) {
      showMessage('Por favor corrige los errores en el formulario', 'error');
      return;
    }

    setConfirmAction('email');
    setConfirmData({ newEmail });
    setShowConfirmModal(true);
  }, [newEmail, isEmailFormValid, showMessage]);

  // Función para guardar email
  const handleSaveEmail = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        showMessage('Error: No se pudo obtener el token de autenticación', 'error');
        navigate("/authentication/sign-in");
        return;
      }

      const profesionalId = localStorage.getItem('id');

      if (!profesionalId) {
        showMessage('Error: No se encontró el ID del profesional', 'error');
        return;
      }

      const response = await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: confirmData.newEmail })
      });

      if (response.ok) {
        // Actualizar el contexto local
        updateLocalData({ email: confirmData.newEmail });
        setUserData(prev => ({ ...prev, email: confirmData.newEmail }));
        setEmailModal(false);
        setShowConfirmModal(false);
        // Resetear campos después del envío exitoso
        setNewEmail('');
        setConfirmEmail('');
        setEmailError('');
        setConfirmEmailError('');
        showMessage('Correo electrónico actualizado correctamente');
      } else {
        const errorData = await response.json();
        showMessage(errorData.error || 'Error al actualizar el correo', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('Error de conexión. Intenta nuevamente', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [confirmData.newEmail, baseURL, showMessage, getToken, navigate, updateLocalData]);

  // Función para confirmar guardar contraseña
  const handleConfirmSavePassword = useCallback(() => {
    if (!newPassword.trim()) {
      showMessage('Por favor ingresa una nueva contraseña', 'error');
      return;
    }

    if (newPassword.length < 8) {
      showMessage('La nueva contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage('Las contraseñas no coinciden', 'error');
      return;
    }

    setConfirmAction('password');
    setConfirmData({ newPassword });
    setShowConfirmModal(true);
  }, [newPassword, confirmPassword, showMessage]);

  // Función para guardar contraseña
  const handleSavePassword = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        showMessage('Error: No se pudo obtener el token de autenticación', 'error');
        navigate("/authentication/sign-in");
        return;
      }

      const profesionalId = localStorage.getItem('id');

      if (!profesionalId) {
        showMessage('Error: No se encontró el ID del profesional', 'error');
        return;
      }

      const response = await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contraseña: confirmData.newPassword
        })
      });

      if (response.ok) {
        setPasswordModal(false);
        setShowConfirmModal(false);
        // Resetear campos después del envío exitoso
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswords({ new: false, confirm: false });
        showMessage('Contraseña actualizada correctamente');
      } else {
        const errorData = await response.json();
        showMessage(errorData.error || 'Error al actualizar la contraseña', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('Error de conexión. Intenta nuevamente', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [confirmData.newPassword, baseURL, showMessage, getToken, navigate]);

  // Función para confirmar guardar teléfono
  const handleConfirmSavePhone = useCallback(() => {
    if (!newPhone.trim()) {
      showMessage('Por favor ingresa un número de teléfono válido', 'error');
      return;
    }

    if (!confirmPhone.trim()) {
      showMessage('Por favor confirma el número de teléfono', 'error');
      return;
    }

    // Validar formato: debe empezar con 09 y tener exactamente 9 dígitos
    const phoneRegex = /^09\d{7}$/;
    if (!phoneRegex.test(newPhone)) {
      showMessage('El número debe empezar con 09 y tener exactamente 9 dígitos', 'error');
      return;
    }

    if (!phoneRegex.test(confirmPhone)) {
      showMessage('El número de confirmación debe empezar con 09 y tener exactamente 9 dígitos', 'error');
      return;
    }

    // Verificar si hay errores de validación en tiempo real
    if (phoneError || confirmPhoneError) {
      showMessage('Por favor corrige los errores en los números de teléfono', 'error');
      return;
    }

    if (newPhone !== confirmPhone) {
      showMessage('Los números de teléfono no coinciden', 'error');
      return;
    }

    if (newPhone === userData.celular) {
      showMessage('El nuevo número debe ser diferente al actual', 'error');
      return;
    }

    setConfirmAction('phone');
    setConfirmData({ newPhone });
    setShowConfirmModal(true);
  }, [newPhone, confirmPhone, userData.celular, phoneError, confirmPhoneError, showMessage]);

  // Función para guardar teléfono
  const handleSavePhone = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        showMessage('Error: No se pudo obtener el token de autenticación', 'error');
        navigate("/authentication/sign-in");
        return;
      }

      const profesionalId = localStorage.getItem('id');

      if (!profesionalId) {
        showMessage('Error: No se encontró el ID del profesional', 'error');
        return;
      }

      const response = await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ celular: confirmData.newPhone })
      });

      if (response.ok) {
        // Actualizar el contexto local
        updateLocalData({ celular: confirmData.newPhone });
        setUserData(prev => ({ ...prev, celular: confirmData.newPhone }));
        setPhoneModal(false);
        setShowConfirmModal(false);
        // Resetear campos después del envío exitoso
        setNewPhone('');
        setConfirmPhone('');
        setPhoneError('');
        setConfirmPhoneError('');
        showMessage('Número de celular actualizado correctamente');
      } else {
        const errorData = await response.json();
        showMessage(errorData.error || 'Error al actualizar el número', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('Error de conexión. Intenta nuevamente', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [confirmData.newPhone, baseURL, showMessage, getToken, navigate, updateLocalData]);

  // Funciones para manejar el modal de confirmación
  const handleConfirmSave = useCallback(() => {
    if (confirmAction === 'email') {
      handleSaveEmail();
    } else if (confirmAction === 'password') {
      handleSavePassword();
    } else if (confirmAction === 'phone') {
      handleSavePhone();
    }
  }, [confirmAction, handleSaveEmail, handleSavePassword, handleSavePhone]);

  const handleCancelSave = useCallback(() => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData({});
  }, []);

  // Función para calcular la posición del modal basada en el botón de email
  const calculateModalPosition = useCallback(() => {
    if (emailButtonRef.current) {
      const rect = emailButtonRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const topPosition = rect.top + scrollTop;
      setModalTopPosition(topPosition);
    }
  }, []);

  // Funciones para cerrar modales y limpiar formularios
  const closeEmailModal = useCallback(() => {
    setEmailModal(false);
    setNewEmail('');
    setConfirmEmail('');
    setEmailError('');
    setConfirmEmailError('');
  }, []);

  const closePasswordModal = useCallback(() => {
    setPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
  }, []);

  const closePhoneModal = useCallback(() => {
    setPhoneModal(false);
    setNewPhone('');
    setConfirmPhone('');
    setPhoneError('');
    setConfirmPhoneError('');
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white', padding: '20px', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
        <IconButton onClick={() => navigate('/perfil')} sx={{ color: "#000", padding: '8px', marginRight: '16px' }}>
          <ArrowBackIcon />
        </IconButton>
        <h1 style={{ fontWeight: 'bold', color: '#000', fontSize: '20px', margin: 0 }}>Ajustes</h1>
      </div>

      {/* Opciones */}
      <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
        <div
          ref={emailButtonRef}
          onClick={() => {
            calculateModalPosition();
            // Resetear campos al abrir el modal
            setNewEmail('');
            setConfirmEmail('');
            setEmailError('');
            setConfirmEmailError('');
            setEmailModal(true);
          }}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ marginRight: '16px', display: 'flex', alignItems: 'center' }}>
            <EmailIcon />
          </div>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: '500' }}>Correo electrónico</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: '#666', fontSize: '20px' }} />
        </div>

        <div
          onClick={() => {
            calculateModalPosition();
            // Resetear campos al abrir el modal
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswords({ new: false, confirm: false });
            setPasswordModal(true);
          }}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ marginRight: '16px', display: 'flex', alignItems: 'center' }}>
            <LockIcon />
          </div>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: '500' }}>Contraseña</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: '#666', fontSize: '20px' }} />
        </div>

        <div
          onClick={() => {
            calculateModalPosition();
            // Resetear campos al abrir el modal
            setNewPhone('');
            setConfirmPhone('');
            setPhoneError('');
            setConfirmPhoneError('');
            setPhoneModal(true);
          }}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ marginRight: '16px', display: 'flex', alignItems: 'center' }}>
            <PhoneIcon />
          </div>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#000', fontSize: '16px', fontWeight: '500' }}>Número de celular</Typography>
          </Box>
          <ChevronRightIcon sx={{ color: '#666', fontSize: '20px' }} />
        </div>
      </div>

      {/* Logout */}
      <div style={{ width: '100%', maxWidth: '400px', margin: '280px auto 0', textAlign: 'center' }}>
        <Typography onClick={handleLogout} sx={{ color: '#d32f2f', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>
          Cerrar sesión
        </Typography>
      </div>

      {/* Toast mejorado */}
      <Toast
        message={message}
        type={messageType}
        isVisible={!!message}
        onClose={() => setMessage('')}
        duration={5000}
        position="top-center"
      />

      {/* Modales */}
      <CustomModal
        isOpen={emailModal}
        onClose={closeEmailModal}
        title="Cambiar correo electrónico"
        onSave={handleConfirmSaveEmail}
        isLoading={isLoading}
        isFormValid={isEmailFormValid}
        alignTop
        topPosition={modalTopPosition}
      >
        <div style={{ marginBottom: '20px' }}>
          <Typography sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '8px'
          }}>
            Actual:
          </Typography>
          <Typography sx={{
            fontSize: '16px',
            color: '#666',
            padding: '12px 16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #ddd'
          }}>
            {userData.email}
          </Typography>
        </div>
        <CustomInput
          label="Nuevo correo electrónico"
          type="email"
          value={newEmail}
          onChange={(e) => {
            setNewEmail(e.target.value);
            validateEmailRealTime(e.target.value, setEmailError);
          }}
          placeholder="Ingresa tu nuevo correo electrónico"
        />
        {emailError && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            {emailError}
          </div>
        )}
        <CustomInput
          label="Confirmar nuevo correo electrónico"
          type="email"
          value={confirmEmail}
          onChange={(e) => {
            setConfirmEmail(e.target.value);
            validateEmailRealTime(e.target.value, setConfirmEmailError);
          }}
          placeholder="Confirma tu nuevo correo electrónico"
        />
        {confirmEmailError && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            {confirmEmailError}
          </div>
        )}
        {newEmail && confirmEmail && newEmail !== confirmEmail && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            Los correos electrónicos no coinciden
          </div>
        )}
        {newEmail && newEmail === userData.email && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            El nuevo correo debe ser diferente al actual
          </div>
        )}
      </CustomModal>

      <CustomModal
        isOpen={passwordModal}
        onClose={closePasswordModal}
        title="Cambiar contraseña"
        onSave={handleConfirmSavePassword}
        isLoading={isLoading}
        isFormValid={isPasswordFormValid}
        alignTop
        topPosition={modalTopPosition}
      >
        <CustomInput
          label="Nueva contraseña"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          showPassword={showPasswords.new}
          onTogglePassword={() => togglePasswordVisibility('new')}
          placeholder="Ingresa tu nueva contraseña"
        />
        {newPassword && newPassword.length < 8 && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            La contraseña debe tener al menos 8 caracteres
          </div>
        )}
        <CustomInput
          label="Confirmar nueva contraseña"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          showPassword={showPasswords.confirm}
          onTogglePassword={() => togglePasswordVisibility('confirm')}
          placeholder="Confirma tu nueva contraseña"
        />
        {newPassword && confirmPassword && newPassword !== confirmPassword && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            Las contraseñas no coinciden
          </div>
        )}
      </CustomModal>

      <CustomModal
        isOpen={phoneModal}
        onClose={closePhoneModal}
        title="Cambiar número de celular"
        onSave={handleConfirmSavePhone}
        isLoading={isLoading}
        isFormValid={isPhoneFormValid}
        alignTop
        topPosition={modalTopPosition}
      >
        <div style={{ marginBottom: '20px' }}>
          <Typography sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '8px'
          }}>
            Actual:
          </Typography>
          <Typography sx={{
            fontSize: '16px',
            color: '#666',
            padding: '12px 16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #ddd'
          }}>
            {userData.celular}
          </Typography>
        </div>
        <CustomInput
          label="Nuevo número de celular"
          type="tel"
          value={newPhone}
          onChange={(e) => {
            const validatedPhone = validatePhone(e.target.value, setPhoneError);
            setNewPhone(validatedPhone);
          }}
          placeholder="Ingresa tu nuevo número de celular"
        />
        {phoneError && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            {phoneError}
          </div>
        )}
        <CustomInput
          label="Confirmar nuevo número de celular"
          type="tel"
          value={confirmPhone}
          onChange={(e) => {
            const validatedPhone = validatePhone(e.target.value, setConfirmPhoneError);
            setConfirmPhone(validatedPhone);
          }}
          placeholder="Confirma tu nuevo número de celular"
        />
        {confirmPhoneError && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            {confirmPhoneError}
          </div>
        )}
        {newPhone && confirmPhone && newPhone !== confirmPhone && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            Los números de celular no coinciden
          </div>
        )}
        {newPhone && newPhone === userData.celular && (
          <div style={{
            color: '#d32f2f',
            fontSize: '12px',
            marginTop: '-15px',
            marginBottom: '15px'
          }}>
            El nuevo número debe ser diferente al actual
          </div>
        )}
      </CustomModal>

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 10000,
          padding: getTopModalPadding(modalTopPosition)
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }}>
            <Typography variant="h6" sx={{
              color: '#000',
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Confirmar cambios
            </Typography>

            <Typography variant="body1" sx={{
              color: '#666',
              fontSize: '14px',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              {confirmAction === 'email' && (
                <>¿Estás seguro de que quieres cambiar tu correo electrónico a <strong>{confirmData.newEmail}</strong>?</>
              )}
              {confirmAction === 'password' && (
                <>¿Estás seguro de que quieres cambiar tu contraseña?</>
              )}
              {confirmAction === 'phone' && (
                <>¿Estás seguro de que quieres cambiar tu número de celular a <strong>{confirmData.newPhone}</strong>?</>
              )}
            </Typography>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleConfirmSave}
                disabled={isLoading}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '12px',
                  backgroundColor: isLoading ? '#ccc' : '#1F85F6',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textTransform: 'none'
                }}
              >
                {isLoading ? 'Guardando...' : 'Confirmar'}
              </button>

              <button
                onClick={handleCancelSave}
                disabled={isLoading}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #ccc',
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  color: '#666',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textTransform: 'none'
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ajustes;
